// Tour guiado del editor de video (primera vez), con "spotlight".
//
// Usa driver.js: oscurece la pantalla, ilumina SOLO el elemento del paso y
// muestra un tooltip al lado con un botón para avanzar. Reemplaza al slideshow
// simple anterior (icono + texto) por algo más claro para el usuario nuevo.
//
// Los targets se referencian por `data-tour="..."` (agregados en VideoEditor).
// Todos son visibles en la pantalla principal del editor, así que el tour no
// necesita abrir paneles. El "mostrar una sola vez" lo maneja el caller.

import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./editorTour.css"; // tema ATLAS (pisa los estilos por defecto)

/**
 * Lanza el tour del editor. `onDone` se llama cuando el tour termina, sea
 * porque el usuario lo completó o lo salteó (cerró) — así se marca como visto
 * y no vuelve a aparecer.
 */
export function startEditorTour(onDone?: () => void): void {
  const d = driver({
    showProgress: true,
    progressText: "{{current}} de {{total}}",
    nextBtnText: "Entendido",
    prevBtnText: "Atrás",
    doneBtnText: "Listo",
    overlayColor: "#02062B", // brand-black
    overlayOpacity: 0.72,
    allowClose: true, // click afuera / Esc para saltar
    popoverClass: "atlas-tour",
    onDestroyed: () => onDone?.(),
    steps: [
      {
        element: '[data-tour="director"]',
        popover: {
          title: "Director de estudio",
          description:
            "Tu asistente. Pedile lo que quieras —“hacé un video de mi producto”— y te arma el plan, sugiere ideas y genera las escenas por vos.",
          side: "left",
          align: "start",
        },
      },
      {
        element: '[data-tour="generate"]',
        popover: {
          title: "Generar con IA",
          description:
            "Creá escenas de video, imágenes y diálogos con personas o modelos. Vos describís, la IA lo genera.",
          side: "right",
          align: "start",
        },
      },
      {
        element: '[data-tour="files"]',
        popover: {
          title: "Archivos del proyecto",
          description:
            "Tu material: subí videos e imágenes, o usá tus productos, escenas e historial generado.",
          side: "right",
          align: "start",
        },
      },
      {
        element: '[data-tour="brand"]',
        popover: {
          title: "Tu marca",
          description:
            "Tu logo, colores y tipografías, listos para aplicar a tu video y mantener tu identidad.",
          side: "right",
          align: "start",
        },
      },
      {
        element: '[data-tour="timeline"]',
        popover: {
          title: "Línea de tiempo",
          description:
            "Acá montás y editás tu video: ordenás los clips, ajustás duraciones y sumás texto y transiciones.",
          side: "top",
          align: "center",
        },
      },
      {
        element: '[data-tour="export"]',
        popover: {
          title: "Exportar",
          description:
            "Cuando tu video esté listo, lo descargás desde acá para publicarlo.",
          side: "bottom",
          align: "end",
        },
      },
    ],
  });
  d.drive();
}

/**
 * Mini-tour CONTEXTUAL: se dispara la primera vez que el usuario entra a una
 * pantalla/modo puntual (ej. Transiciones, Modo Diálogo, generar audio). Es
 * "just-in-time" — corto, 1-3 pasos. Mismo spotlight + tema ATLAS que el tour
 * principal.
 *
 * El "ya lo vi" lo maneja el CALLER (gatea con isSeen antes de llamar, y pasa
 * `onDone` para marcarlo con markSeen → persiste en la DB + localStorage). Acá
 * solo se chequea que el primer elemento esté montado en el DOM: si no está,
 * no arranca y NO llama a onDone (así no se marca visto algo que no se mostró).
 */
export function startContextualTour(opts: {
  steps: DriveStep[];
  onDone?: () => void;
}): void {
  const { steps, onDone } = opts;
  if (steps.length === 0) return;
  // El primer paso tiene que existir en el DOM (el panel ya montado).
  const firstSel =
    typeof steps[0].element === "string" ? (steps[0].element as string) : null;
  if (firstSel && !document.querySelector(firstSel)) return;

  const d = driver({
    showProgress: steps.length > 1,
    progressText: "{{current}} de {{total}}",
    nextBtnText: "Siguiente",
    prevBtnText: "Atrás",
    doneBtnText: "Entendido",
    overlayColor: "#02062B",
    overlayOpacity: 0.72,
    allowClose: true,
    popoverClass: "atlas-tour",
    onDestroyed: () => onDone?.(),
    steps,
  });
  d.drive();
}
